import React, { useState } from "react";
import { Plus, X, ChevronLeft } from "lucide-react";
import { toast } from "sonner";
import { axiosAuth } from "../shared/axiosAuth";
import { API } from "../data/constants";

export const HappyEndingsPage = ({ happyEndings, fetchHappyEndings, isAdmin }) => {
  const [showForm, setShowForm] = useState(false);
  const [editingStory, setEditingStory] = useState(null);
  const [viewingStory, setViewingStory] = useState(null);
  const [formData, setFormData] = useState({ title: "", content: "", author_name: "", pet_name: "", photo: "", audio_file: "", audio_filename: "", tags: [] });
  const [loading, setLoading] = useState(false);

  const tagOptions = ["Rescue", "Adoption", "Healing", "Miracle", "Rainbow Bridge", "New Beginnings", "Friendship", "Love"];

  const resetForm = () => { 
    setFormData({ title: "", content: "", author_name: "", pet_name: "", photo: "", audio_file: "", audio_filename: "", tags: [] }); 
    setEditingStory(null); 
    setShowForm(false); 
  };

  const handleImageUpload = (e) => { 
    const file = e.target.files[0]; 
    if (file) { 
      const reader = new FileReader(); 
      reader.onloadend = () => setFormData({ ...formData, photo: reader.result }); 
      reader.readAsDataURL(file); 
    } 
  };
  
  const handleAudioUpload = (e) => { 
    const file = e.target.files[0]; 
    if (file && file.size <= 15 * 1024 * 1024) { 
      const reader = new FileReader(); 
      reader.onloadend = () => setFormData({ ...formData, audio_file: reader.result, audio_filename: file.name }); 
      reader.readAsDataURL(file); 
    } else { 
      toast.error("Audio file too large (max 15MB)"); 
    } 
  };

  const toggleTag = (tag) => {
    const currentTags = formData.tags || [];
    if (currentTags.includes(tag)) {
      setFormData({ ...formData, tags: currentTags.filter(t => t !== tag) });
    } else {
      setFormData({ ...formData, tags: [...currentTags, tag] });
    }
  };

  const handleSave = async () => {
    if (!formData.title.trim() || !formData.content.trim()) { 
      toast.error("Title and story content are required"); 
      return; 
    }
    setLoading(true);
    try {
      if (editingStory) {
        await axiosAuth.put(`${API}/happy-endings/${editingStory.id}`, formData);
        toast.success("Story updated! 📖");
      } else {
        await axiosAuth.post(`${API}/happy-endings`, formData);
        toast.success("Happy ending added! 🌈");
      }
      await fetchHappyEndings();
      resetForm();
    } catch (error) { 
      toast.error("Failed to save story"); 
    }
    finally { setLoading(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this story?")) return;
    try {
      await axiosAuth.delete(`${API}/happy-endings/${id}`);
      toast.success("Story deleted");
      await fetchHappyEndings();
    } catch (error) { 
      toast.error("Failed to delete"); 
    }
  };

  const handleEdit = (story) => { 
    setFormData({ ...story, tags: story.tags || [] }); 
    setEditingStory(story); 
    setShowForm(true); 
  };

  // View a single story
  if (viewingStory) {
    return (
      <div data-testid="happy-endings-page" className="fade-in relative z-10">
        <button onClick={() => setViewingStory(null)} className="mb-6 text-sage hover:underline flex items-center gap-2">
          <ChevronLeft size={20} /> Back to Stories
        </button>
        <div className="bg-white/90 backdrop-blur-sm rounded-3xl p-8 border border-white/50 max-w-3xl mx-auto">
          {viewingStory.photo && (
            <div className="w-full h-64 rounded-2xl overflow-hidden mb-6">
              <img src={viewingStory.photo} alt={viewingStory.title} className="w-full h-full object-cover" />
            </div>
          )}
          <h1 className="font-heading text-3xl font-bold text-charcoal mb-2">{viewingStory.title}</h1>
          {viewingStory.pet_name && <p className="text-sage mb-2">🐾 About: {viewingStory.pet_name}</p>}
          {viewingStory.author_name && <p className="text-stone mb-4">By: {viewingStory.author_name}</p>}
          {viewingStory.tags && viewingStory.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-6">
              {viewingStory.tags.map((tag, i) => (
                <span key={i} className="px-3 py-1 bg-sage/10 text-sage rounded-full text-sm">{tag}</span>
              ))}
            </div>
          )}
          <div className="prose prose-stone max-w-none mb-6">
            <p className="whitespace-pre-wrap leading-relaxed text-charcoal">{viewingStory.content}</p>
          </div>
          {viewingStory.audio_file && (
            <div className="bg-sage/10 rounded-2xl p-4">
              <p className="text-sage font-medium mb-2">🎧 Listen to this story</p>
              <audio controls className="w-full">
                <source src={viewingStory.audio_file} type="audio/mpeg" />
              </audio>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="happy-endings-page" className="fade-in relative z-10">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-charcoal">Happy Endings 🌈</h1>
          <p className="text-stone mt-2">Heartwarming stories of love, rescue, and new beginnings</p>
        </div>
        {isAdmin && (
          <button onClick={() => setShowForm(true)} className="bg-terracotta text-white px-6 py-3 rounded-full font-medium hover:bg-terracotta-dark transition-all flex items-center gap-2">
            <Plus size={20} />Add Story
          </button>
        )}
      </div>

      {/* Add/Edit Form */}
      {showForm && isAdmin && (
        <div className="fixed inset-0 bg-charcoal/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-2xl font-semibold text-charcoal">{editingStory ? "Edit Story" : "Add New Story"} 📖</h2>
              <button onClick={resetForm} className="text-stone hover:text-charcoal p-2"><X size={24} /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-charcoal font-medium mb-2">Story Title *</label>
                <input type="text" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} className="w-full h-12 border-2 border-gray-200 focus:border-sage rounded-xl px-4 outline-none" placeholder="A heartwarming title..." />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-charcoal font-medium mb-2">Pet's Name</label>
                  <input type="text" value={formData.pet_name} onChange={(e) => setFormData({ ...formData, pet_name: e.target.value })} className="w-full h-12 border-2 border-gray-200 focus:border-sage rounded-xl px-4 outline-none" placeholder="e.g., Buddy" />
                </div>
                <div>
                  <label className="block text-charcoal font-medium mb-2">Author Name</label>
                  <input type="text" value={formData.author_name} onChange={(e) => setFormData({ ...formData, author_name: e.target.value })} className="w-full h-12 border-2 border-gray-200 focus:border-sage rounded-xl px-4 outline-none" placeholder="Your name (optional)" />
                </div>
              </div>
              <div>
                <label className="block text-charcoal font-medium mb-2">Story Content *</label>
                <textarea value={formData.content} onChange={(e) => setFormData({ ...formData, content: e.target.value })} rows={8} className="w-full border-2 border-gray-200 focus:border-sage rounded-xl px-4 py-3 outline-none resize-none" placeholder="Tell us the story..." />
              </div>
              <div>
                <label className="block text-charcoal font-medium mb-2">Tags</label>
                <div className="flex flex-wrap gap-2">
                  {tagOptions.map((tag) => (
                    <button key={tag} type="button" onClick={() => toggleTag(tag)} className={`px-3 py-1 rounded-full text-sm font-medium transition-all ${(formData.tags || []).includes(tag) ? "bg-sage text-white" : "bg-gray-100 text-stone hover:bg-gray-200"}`}>{tag}</button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-charcoal font-medium mb-2">Cover Photo</label>
                <div className="flex items-center gap-4">
                  {formData.photo ? (
                    <div className="relative">
                      <img src={formData.photo} alt="Cover" className="w-24 h-24 rounded-xl object-cover" />
                      <button type="button" onClick={() => setFormData({ ...formData, photo: "" })} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"><X size={14} /></button>
                    </div>
                  ) : <div className="w-24 h-24 rounded-xl bg-gray-100 flex items-center justify-center text-3xl">📷</div>}
                  <label className="cursor-pointer bg-paper text-sage border-2 border-sage/20 hover:border-sage px-4 py-2 rounded-full transition-all">
                    <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />Choose Photo
                  </label>
                </div>
              </div>
              <div>
                <label className="block text-charcoal font-medium mb-2">Audio Narration (optional)</label>
                <div className="flex items-center gap-4">
                  {formData.audio_file ? (
                    <div className="flex items-center gap-2 bg-sage/10 px-4 py-2 rounded-full">
                      <span className="text-sage">🎧 {formData.audio_filename || "Audio uploaded"}</span>
                      <button type="button" onClick={() => setFormData({ ...formData, audio_file: "", audio_filename: "" })} className="text-red-500 hover:text-red-600"><X size={16} /></button>
                    </div>
                  ) : (
                    <label className="cursor-pointer bg-paper text-sage border-2 border-sage/20 hover:border-sage px-4 py-2 rounded-full transition-all">
                      <input type="file" accept="audio/*" onChange={handleAudioUpload} className="hidden" />Upload Audio
                    </label>
                  )}
                </div>
                <p className="text-stone text-xs mt-1">Record yourself reading the story (max 15MB)</p>
              </div>
              <button onClick={handleSave} disabled={loading} className="w-full bg-sage text-white py-3 rounded-full font-medium hover:bg-sage-dark transition-all disabled:opacity-50">
                {loading ? "Saving..." : (editingStory ? "Update Story" : "Add Story")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stories Grid */}
      {happyEndings.length === 0 ? (
        <div className="text-center py-16 bg-white/50 rounded-3xl">
          <div className="text-6xl mb-4">🌈</div>
          <h3 className="font-heading text-xl font-semibold text-charcoal mb-2">No stories yet</h3>
          <p className="text-stone">Happy endings will appear here</p>
        </div>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {happyEndings.map((story) => (
            <div key={story.id} className="bg-white/90 backdrop-blur-sm rounded-3xl overflow-hidden border border-white/50 hover:shadow-lg transition-all group">
              <div onClick={() => setViewingStory(story)} className="cursor-pointer">
                <div className="h-48 bg-gradient-to-br from-sage/20 to-amber-100/20 relative">
                  {story.photo ? (
                    <img src={story.photo} alt={story.title} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-5xl">🌈</div>
                  )}
                  {story.audio_file && (
                    <span className="absolute bottom-3 left-3 bg-sage text-white px-3 py-1 rounded-full text-sm flex items-center gap-1">
                      🎧 Audio
                    </span>
                  )}
                </div>
                <div className="p-6">
                  <h3 className="font-heading text-xl font-semibold text-charcoal mb-2">{story.title}</h3>
                  {story.pet_name && <p className="text-sage text-sm mb-2">🐾 {story.pet_name}</p>}
                  <p className="text-stone text-sm line-clamp-3">{story.content}</p>
                  {story.tags && story.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-3">
                      {story.tags.slice(0, 3).map((tag, i) => (
                        <span key={i} className="px-2 py-0.5 bg-sage/10 text-sage rounded-full text-xs">{tag}</span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              {isAdmin && (
                <div className="px-6 pb-4 flex gap-2">
                  <button onClick={() => handleEdit(story)} className="flex-1 py-2 text-sage hover:bg-sage/10 rounded-full transition-all text-sm">Edit</button>
                  <button onClick={() => handleDelete(story.id)} className="flex-1 py-2 text-red-500 hover:bg-red-50 rounded-full transition-all text-sm">Delete</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default HappyEndingsPage;
